import React from 'react';
import { Group, Modal, Paper, Stack, Text } from '@mantine/core';

export type TradeTimeExampleMode = 'last' | 'first';

interface TradeTimeExampleModalProps {
  opened: boolean;
  onClose: () => void;
  mode: TradeTimeExampleMode;
  minSeconds?: number | null;
  maxSeconds?: number | null;
}

const formatSeconds = (seconds?: number | null) => {
  if (seconds == null) return 'Off';
  if (seconds % 86400 === 0) return `${seconds / 86400}d`;
  if (seconds % 3600 === 0) return `${seconds / 3600}h`;
  if (seconds % 60 === 0) return `${seconds / 60}m`;
  return `${seconds}s`;
};

const TradeTimeExampleModal: React.FC<TradeTimeExampleModalProps> = ({
  opened,
  onClose,
  mode,
  minSeconds,
  maxSeconds,
}) => {
  const isLastTrade = mode === 'last';
  const title = isLastTrade ? 'Last Trade — time window' : 'First Trade — wallet age';
  const eventName = isLastTrade ? 'last swap' : 'first swap';
  const leftBoundary = isLastTrade ? 'Max 24h' : 'Min 30d';
  const rightBoundary = isLastTrade ? 'Min 1h' : 'Max 7d';
  const allowedFormula = isLastTrade
    ? 'Min ≤ time since Last Trade < Max'
    : 'Max ≤ time since First Trade ≤ Min';
  const currentRangeInvalid = minSeconds != null && maxSeconds != null && (
    isLastTrade ? minSeconds >= maxSeconds : minSeconds < maxSeconds
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      size="md"
      overlayProps={{ opacity: 0.65, blur: 4 }}
      styles={{
        content: { backgroundColor: '#0D1117', border: '1px solid #374151' },
        header: { backgroundColor: '#0D1117', borderBottom: '1px solid #21262D' },
        title: { color: '#F9FAFB', fontWeight: 700, fontSize: 18 },
        close: { color: '#9CA3AF' },
        body: { padding: 20 },
      }}
    >
      <Stack gap="md">
        <Text size="sm" c="#CBD5E1" lh={1.5}>
          Both fields describe how long ago the {eventName} happened. Time gets older as you move
          left from <strong>Now</strong>.
        </Text>

        <Paper
          p="md"
          radius="md"
          style={{ backgroundColor: '#07090E', border: '1px solid #2D3748' }}
        >
          <Group justify="space-between" mb={4}>
            <Text size="10px" c="#6B7280">OLDER</Text>
            <Text size="10px" c="#9CA3AF">NOW</Text>
          </Group>

          <div style={{ position: 'relative', height: 112, margin: '0 10px' }}>
            <div style={{
              position: 'absolute',
              top: 37,
              left: 0,
              right: 0,
              height: 2,
              backgroundColor: '#4B5563',
            }} />
            <div style={{
              position: 'absolute',
              top: 35,
              left: '27%',
              width: '49%',
              height: 6,
              borderRadius: 3,
              backgroundColor: '#6366F1',
            }} />

            {[
              { left: '12%', label: 'Too old', color: '#6B7280' },
              { left: '51%', label: 'Included', color: '#10B981' },
              { left: '88%', label: 'Too recent', color: '#6B7280' },
            ].map(point => (
              <React.Fragment key={point.label}>
                <div style={{
                  position: 'absolute',
                  top: 31,
                  left: point.left,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: point.color,
                  transform: 'translateX(-50%)',
                }} />
                <Text
                  component="span"
                  size="9px"
                  fw={point.label === 'Included' ? 700 : 500}
                  c={point.label === 'Included' ? '#6EE7B7' : '#6B7280'}
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: point.left,
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {point.label}
                </Text>
              </React.Fragment>
            ))}

            <div style={{
              position: 'absolute',
              top: 27,
              left: '27%',
              width: 2,
              height: 22,
              backgroundColor: '#A5B4FC',
            }} />
            <div style={{
              position: 'absolute',
              top: 27,
              left: '76%',
              width: 2,
              height: 22,
              backgroundColor: '#A5B4FC',
            }} />

            <Text
              component="span"
              size="10px"
              fw={700}
              c="#A5B4FC"
              style={{ position: 'absolute', top: 56, left: '27%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}
            >
              {leftBoundary}
            </Text>
            <Text
              component="span"
              size="10px"
              fw={700}
              c="#A5B4FC"
              style={{ position: 'absolute', top: 56, left: '76%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}
            >
              {rightBoundary}
            </Text>

            <Text
              component="span"
              size="10px"
              c="#C7D2FE"
              style={{
                position: 'absolute',
                top: 82,
                left: '51.5%',
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
              }}
            >
              allowed window
            </Text>
          </div>

          <Paper
            px="sm"
            py={8}
            radius="sm"
            style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.25)' }}
          >
            <Text size="xs" fw={700} ta="center" c="#C7D2FE">
              {allowedFormula}
            </Text>
          </Paper>
        </Paper>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Paper p="sm" radius="md" style={{ backgroundColor: '#161B22', border: '1px solid #374151' }}>
            <Text size="10px" fw={700} c="#A5B4FC" mb={4}>ONLY MIN SET</Text>
            <Text size="xs" c="#D1D5DB" lh={1.4}>
              {isLastTrade
                ? 'Last trade must be at least Min ago.'
                : 'First trade must be no more than Min ago.'}
            </Text>
          </Paper>
          <Paper p="sm" radius="md" style={{ backgroundColor: '#161B22', border: '1px solid #374151' }}>
            <Text size="10px" fw={700} c="#A5B4FC" mb={4}>ONLY MAX SET</Text>
            <Text size="xs" c="#D1D5DB" lh={1.4}>
              {isLastTrade
                ? 'Last trade must be within the past Max.'
                : 'First trade must be at least Max ago.'}
            </Text>
          </Paper>
        </div>

        <Paper
          p="sm"
          radius="md"
          style={{
            backgroundColor: currentRangeInvalid ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.07)',
            border: currentRangeInvalid ? '1px solid rgba(239, 68, 68, 0.35)' : '1px solid rgba(16, 185, 129, 0.24)',
          }}
        >
          <Group justify="space-between" gap="xs" wrap="wrap">
            <Text size="xs" fw={700} c="#E5E7EB">Current preset</Text>
            <Text size="xs" ff="monospace" c={currentRangeInvalid ? '#FCA5A5' : '#9CA3AF'}>
              Min {formatSeconds(minSeconds)} · Max {formatSeconds(maxSeconds)}
            </Text>
          </Group>
          {currentRangeInvalid && (
            <Text size="10px" c="#FCA5A5" mt={5}>
              {isLastTrade
                ? 'This range is reversed: Last Trade Min must be smaller than Max.'
                : 'This range is reversed: First Trade Min must be greater than or equal to Max.'}
            </Text>
          )}
        </Paper>

        {!isLastTrade && (
          <Text size="xs" c="#FBBF24" lh={1.45}>
            The First Trade names look reversed on a timeline: <strong>Min</strong> is the older left
            boundary, while <strong>Max</strong> is the newer right boundary.
          </Text>
        )}
      </Stack>
    </Modal>
  );
};

export default TradeTimeExampleModal;
